import React from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Line, ComposedChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { campaigns, opportunities } from '../data/demandData';
import { demandTrend, socialSnapshot } from '../data/demandBriefing';
const currency = (value: number) => value.toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
type SnapshotProps = {
  onGoToOpportunities: () => void;
  onGoToCampaigns: () => void;
  onGoToSocial: () => void;
};
export function DemandSnapshots({
  onGoToOpportunities,
  onGoToCampaigns,
  onGoToSocial
}: SnapshotProps) {
  const topOpportunities = [...opportunities].sort((a, b) => Number(b.recovery.replace(/[^0-9.]/g, '')) - Number(a.recovery.replace(/[^0-9.]/g, ''))).slice(0, 3);
  const live = campaigns.filter(campaign => campaign.status === 'Live');
  const winning = live.filter(campaign => campaign.spend > 0 && campaign.revenue / campaign.spend >= 4);
  const struggling = campaigns.filter(campaign => campaign.spend > 0 && campaign.revenue / campaign.spend < 3.5);
  return <div className="space-y-8" data-id="element-1006" data-mp="hfyn40fhlzfv">
      <Module title="Demand performance" caption="Revenue, spend, and units moved over the last six months" action="View campaigns" onAction={onGoToCampaigns} data-id="element-1007" data-mp="hc155i4nbdxk">
        <div className="h-64 w-full" data-id="element-1008" data-mp="7gpy7lzhbb0d">
          <ResponsiveContainer width="100%" height="100%" data-id="element-1009" data-mp="v5sd4m2qapr6">
            <ComposedChart data={demandTrend} margin={{
            top: 8,
            right: 8,
            bottom: 0,
            left: -12
          }} data-id="element-1010" data-mp="v6bwm7811m7b">
              <CartesianGrid stroke="#1a1a1a" strokeOpacity={0.07} vertical={false} data-id="element-1011" data-mp="104dfar1jjji9" />
              <XAxis dataKey="month" tickLine={false} axisLine={false} tick={{
              fontSize: 12,
              fill: '#1a1a1acc'
            }} data-id="element-1012" data-mp="mcf9ap34m3qb" />
              <YAxis tickLine={false} axisLine={false} tick={{
              fontSize: 12,
              fill: '#1a1a1a99'
            }} tickFormatter={(value: number) => `$${Math.round(value / 1000)}K`} data-id="element-1013" data-mp="qdis9kgh0fvr" />
              <Tooltip cursor={{
              fill: '#1a1a1a0a'
            }} contentStyle={{
              borderRadius: 12,
              border: '1px solid rgba(26,26,26,0.1)',
              fontSize: 12
            }} formatter={(value: number, name: string) => name === 'Units' ? [value.toLocaleString('en-US'), name] : [currency(value), name]} data-id="element-1014" data-mp="1sr3dc1ol4s0y" />
              <Bar dataKey="revenue" name="Revenue" fill="#BB5E40" radius={[5, 5, 0, 0]} barSize={26} data-id="element-1015" data-mp="1eqsk4rzsz1cm" />
              <Bar dataKey="spend" name="Spend" fill="#1a1a1a" fillOpacity={0.2} radius={[5, 5, 0, 0]} barSize={26} data-id="element-1016" data-mp="19takjs7odyha" />
              <Line dataKey="units" name="Units" stroke="#2F5D50" strokeWidth={2} dot={false} yAxisId={0} data-id="element-1017" data-mp="150hhr9vrp07u" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Module>

      <div className="grid gap-6 lg:grid-cols-2" data-id="element-1018" data-mp="w2xdrt6isptj">
        <Module title="Top opportunities" caption="Where demand is missing right now" action="View opportunities" onAction={onGoToOpportunities} data-id="element-1019" data-mp="r2j4k7t6n9tx">
          <ul className="divide-y divide-ink/8" data-id="element-1020" data-mp="m3onbaav5tbx">
            {topOpportunities.map(item => <li key={item.id} className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0" data-id="element-1021" data-mp="bygfdg2zwwml">
                <div className="min-w-0" data-id="element-1022" data-mp="h05q0umb38a5">
                  <p className="truncate text-sm font-semibold" data-id="element-1023" data-mp="lwmexr1bxqoa">{item.title}</p>
                  <p className="mt-0.5 text-xs text-ink/50" data-id="element-1024" data-mp="qw8n3m2awgq8">
                    {item.brand} · {item.units.toLocaleString('en-US')} units · {item.sellThrough}% vs {item.forecast}% forecast
                  </p>
                </div>
                <span className="shrink-0 text-sm font-semibold tabular-nums text-[#94452E]" data-id="element-1025" data-mp="3ue0o1pyf0b0k">{item.recovery}</span>
              </li>)}
          </ul>
        </Module>

        <Module title="Campaign snapshot" caption="What is winning, losing, and needs attention" action="View campaigns" onAction={onGoToCampaigns} data-id="element-1026" data-mp="7xwfi4r1fewr">
          <div className="grid grid-cols-3 gap-3" data-id="element-1027" data-mp="36n4o25jt29i8">
            <Pill label="Winning" value={String(winning.length)} tone="good" data-id="element-1028" data-mp="3bly1xz3082ix" />
            <Pill label="Need attention" value={String(struggling.length)} tone="warn" data-id="element-1029" data-mp="1h6968hypi7u7" />
            <Pill label="Live" value={String(live.length)} tone="plain" data-id="element-1030" data-mp="1h3wnw3p1dvgm" />
          </div>
          <ul className="mt-4 divide-y divide-ink/8" data-id="element-1031" data-mp="1c53byfpeecik">
            {live.slice(0, 3).map(campaign => {
            const roas = campaign.spend > 0 ? campaign.revenue / campaign.spend : 0;
            return <li key={campaign.id} className="flex items-center justify-between gap-4 py-3 last:pb-0" data-id="element-1032" data-mp="1zwqv2bgf4jaa">
                  <div className="min-w-0" data-id="element-1033" data-mp="1vvfdmt7p74d6">
                    <p className="truncate text-sm font-semibold" data-id="element-1034" data-mp="1vudq3p9hwpek">{campaign.name}</p>
                    <p className="mt-0.5 text-xs text-ink/50" data-id="element-1035" data-mp="29tvieem02kso">{campaign.brand} · {campaign.channel}</p>
                  </div>
                  <span className={`shrink-0 text-sm font-semibold tabular-nums ${roas >= 4 ? 'text-forest' : 'text-[#8A6115]'}`} data-id="element-1036" data-mp="24wmzgbt12cqd">
                    {roas.toFixed(1)}x
                  </span>
                </li>;
          })}
          </ul>
        </Module>
      </div>

      <Module title="Social snapshot" caption="Audience growth and organic contribution" action="View social" onAction={onGoToSocial} data-id="element-1037" data-mp="2m6h3xii00xtl">
        <dl className="grid gap-5 sm:grid-cols-3" data-id="element-1038" data-mp="2r63j3xhe0o51">
          {socialSnapshot.map(stat => <div key={stat.label} data-id="element-1039" data-mp="2cdig42ubtbb1">
              <dt className="text-[11px] font-semibold uppercase tracking-wider text-ink/45" data-id="element-1040" data-mp="2hdxosh3nxumo">{stat.label}</dt>
              <dd className="mt-1.5 font-display text-2xl font-semibold tracking-tight" data-id="element-1041" data-mp="23eet4ns1gbr9">{stat.value}</dd>
              <p className="mt-1 text-xs leading-relaxed text-ink/50" data-id="element-1042" data-mp="28ess7gbnwbd5">{stat.detail}</p>
            </div>)}
        </dl>
      </Module>
    </div>;
}
function Module({
  title,
  caption,
  action,
  onAction,
  children
}: {
  title: string;
  caption: string;
  action: string;
  onAction: () => void;
  children: React.ReactNode;
}) {
  return <section className="rounded-2xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-6" data-id="element-1043" data-mp="3nsov50bk27o3">
      <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-1044" data-mp="3sri9sw2kw4pu">
        <div data-id="element-1045" data-mp="3re0gvv5xvsbv">
          <h3 className="font-display text-lg font-medium tracking-tight" data-id="element-1046" data-mp="3racncw0hv5x5">{title}</h3>
          <p className="mt-1 text-xs leading-relaxed text-ink/55" data-id="element-1047" data-mp="3mbrop10w4uat">{caption}</p>
        </div>
      </div>
      <div className="mt-5" data-id="element-1048" data-mp="3hejepnpwbwkn">{children}</div>
      <button type="button" onClick={onAction} className="mt-5 inline-flex items-center gap-1.5 border-t border-ink/8 pt-4 text-sm font-semibold text-[#94452E] transition-colors hover:text-[#BB5E40]" data-id="element-1049" data-mp="3dcxufhb18g0f">
        {action}
        <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1050" data-mp="8yzokxkiuu9t" />
      </button>
    </section>;
}
function Pill({
  label,
  value,
  tone
}: {
  label: string;
  value: string;
  tone: 'good' | 'warn' | 'plain';
}) {
  const styles = {
    good: 'border-forest/25 bg-forest/[0.07] text-forest',
    warn: 'border-[#B4801F]/30 bg-[#B4801F]/[0.08] text-[#8A6115]',
    plain: 'border-ink/12 bg-cream/50 text-ink/70'
  }[tone];
  return <div className={`rounded-xl border px-3 py-2.5 text-center ${styles}`} data-id="element-1051" data-mp="rvhfxdvgks5j">
      <p className="font-display text-xl font-semibold tracking-tight" data-id="element-1052" data-mp="2alwnlokzootl">{value}</p>
      <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wider" data-id="element-1053" data-mp="2fjya6jhmj69t">{label}</p>
    </div>;
}